import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Printer, RefreshCw, Search, AlertCircle, Package, Box, Download, Upload, ArrowRightLeft, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as printerApi from '@/services/printerApi';
import { generateTsplFromLabelData, PRINTER_CONFIGS } from '@/utils/tsplGenerator';

/**
 * Bulk Resticker Staging operator page.
 *
 * Flow:
 *  1. Operator clicks "Top-up Staging" — backend pre-allocates Replace
 *     USNs for every unstickered pack and inserts staging rows.
 *  2. Searchable bin dropdown shows every bin that has staged rows,
 *     oldest first.
 *  3. Operator picks ONE bin → "Print All Labels for This Bin".
 *  4. Backend atomically claims those rows, commits the USN chain,
 *     and returns label payloads.
 *  5. Frontend prints via the connected TSPL printer (or downloads
 *     a .prn fallback) and ACKs back which labels actually came out.
 *  6. Successes flip to 'printed'. Failures bounce back to 'staged'
 *     and the operator can re-run the print — the chain commit is
 *     already done so the same new_usn is reused (no pool waste).
 */
export default function BulkRestickerStaging() {
  const [summary, setSummary] = useState(null);
  const [bins, setBins] = useState([]);
  const [binSearch, setBinSearch] = useState('');
  const [selectedBin, setSelectedBin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [toppingUp, setToppingUp] = useState(false);
  const [recentBatches, setRecentBatches] = useState([]);
  const [printer, setPrinter] = useState(null);  // WebUSB printer device
  const [templates, setTemplates] = useState([]); // for box-sticker template picker

  // Admin-only "re-queue unprinted labels" recovery flow.
  const isAdmin = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'Admin'; }
    catch { return false; }
  }, []);
  const [requeueOpen, setRequeueOpen] = useState(false);
  const [requeuePreview, setRequeuePreview] = useState(null);
  const [requeuing, setRequeuing] = useState(false);

  // ── Phase-B box-sticker prompt state (combo packs only) ──────────────
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const [boxDialogCount, setBoxDialogCount] = useState(0);
  const [boxTemplateId, setBoxTemplateId] = useState('');
  const [boxDevice, setBoxDevice] = useState(null);
  const [boxResolve, setBoxResolve] = useState(null);
  // Design Transfer via staging
  const [dtMapping, setDtMapping] = useState(null);
  const [dtScanInput, setDtScanInput] = useState('');
  const [dtScanning, setDtScanning] = useState(false);
  const [dtUploading, setDtUploading] = useState(false);
  const [dtResults, setDtResults] = useState([]);
  const [dtPendingCount, setDtPendingCount] = useState(0);
  const [dtMinting, setDtMinting] = useState(false);
  const dtFileRef = React.useRef(null);
  const dtScanInputRef = React.useRef(null);

  const refreshDtMapping = useCallback(async () => {
    try {
      const r = await apiClient.get('/resticker/design-transfer/mapping');
      setDtMapping(r.data);
    } catch (e) { /* non-fatal */ }
  }, []);

  const refreshDtPending = useCallback(async () => {
    try {
      const r = await apiClient.get('/resticker/design-transfer/pending');
      setDtPendingCount(r.data.pending_count || 0);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { refreshDtMapping(); refreshDtPending(); }, [refreshDtMapping, refreshDtPending]);

  // Auto-focus the scan box once the mapping is ready (scanner-first workflow).
  useEffect(() => {
    if (dtMapping?.mapping_count) {
      setTimeout(() => dtScanInputRef.current?.focus(), 0);
    }
  }, [dtMapping]);

  const handleUploadDtCsv = async (e) => {
    const file = e.target.files?.[0];
    if (dtFileRef.current) dtFileRef.current.value = '';
    if (!file) return;
    try {
      setDtUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const r = await apiClient.post('/resticker/design-transfer/upload-mapping', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Mapping uploaded: ${r.data.mapping_count} SKU pair(s)${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}`);
      refreshDtMapping();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mapping upload failed');
    } finally {
      setDtUploading(false);
    }
  };

  const handleDtScan = async (value) => {
    const code = (value ?? dtScanInput).trim();
    if (!code) return;
    if (!dtMapping || !dtMapping.mapping_count) {
      toast.error('Upload a source→target mapping CSV first');
      return;
    }
    try {
      setDtScanning(true);
      const r = await apiClient.post('/resticker/design-transfer/stage-scan', { usn_code: code });
      const d = r.data;
      setDtResults((prev) => [{
        old_usn: d.old_usn, new_usn: d.new_usn,
        source_barcode: d.source_barcode, target_barcode: d.target_barcode,
        target_design: d.target_design, target_shade: d.target_shade,
        bin_code: d.bin_code, ok: true, queued: true,
      }, ...prev].slice(0, 100));
      setDtPendingCount((n) => n + 1);
      toast.success(`Queued: ${d.source_barcode} → ${d.target_barcode}`);
      setDtScanInput('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Stage failed';
      setDtResults((prev) => [{ old_usn: code, ok: false, error: msg }, ...prev].slice(0, 100));
      toast.error(msg);
      setDtScanInput('');
    } finally {
      setDtScanning(false);
      // Keep focus on the scan box so the next USN can be scanned immediately.
      setTimeout(() => dtScanInputRef.current?.focus(), 0);
    }
  };

  const handleMintBatch = async () => {
    if (dtPendingCount === 0) {
      toast.message('Nothing queued to mint.');
      return;
    }
    try {
      setDtMinting(true);
      toast.info(`Minting ${dtPendingCount} queued USN(s)…`);
      const r = await apiClient.post('/resticker/design-transfer/mint-batch');
      const { minted, failed, errors, pool_remaining } = r.data;
      if (failed > 0) {
        const sample = (errors || []).slice(0, 3).map(e => `${e.old_usn}: ${e.error}`).join('; ');
        toast.warning(`Minted ${minted}, ${failed} failed. ${sample}`, { duration: 12000 });
      } else {
        toast.success(`Minted ${minted} USN(s). REPLACE pool left: ${pool_remaining}. Now pick the bin and print.`);
      }
      await Promise.all([refreshDtPending(), refresh()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mint batch failed', { duration: 9000 });
    } finally {
      setDtMinting(false);
    }
  };

  // ── Fetch summary + bins on mount + after each action ────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, binRes] = await Promise.all([
        apiClient.get('/resticker/staging/summary'),
        apiClient.get('/resticker/staging/bins', { params: binSearch.trim() ? { q: binSearch.trim() } : {} }),
      ]);
      setSummary(sumRes.data);
      setBins(binRes.data);
    } catch (e) {
      toast.error('Failed to fetch staging data: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [binSearch]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load main-label templates once for the box-sticker picker.
  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get('/main-labels/templates');
        const list = Array.isArray(r.data?.templates) ? r.data.templates : (r.data || []);
        setTemplates(list);
      } catch {
        /* picker falls back to each item's configured box_template */
      }
    })();
  }, []);

  // ── Top-up ───────────────────────────────────────────────────────────
  const handleTopUp = async () => {
    setToppingUp(true);
    try {
      const r = await apiClient.post('/resticker/staging/top-up', {});
      const d = r.data;
      const msg = (
        `Top-up complete: ${d.newly_staged} newly staged` +
        (d.skipped_already_staged ? ` · ${d.skipped_already_staged} already staged` : '') +
        (d.pool_exhausted ? ' · ⚠ Replace pool EXHAUSTED' : '') +
        (d.pool_remaining != null ? ` · pool remaining: ${d.pool_remaining}` : '')
      );
      if (d.pool_exhausted) {
        toast.error(msg, { duration: 12000 });
      } else {
        toast.success(msg, { duration: 6000 });
      }
      await refresh();
    } catch (e) {
      toast.error('Top-up failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setToppingUp(false);
    }
  };

  // ── Re-queue unprinted labels (admin recovery) ───────────────────────
  const handleRequeuePreview = async () => {
    setRequeuing(true);
    try {
      const r = await apiClient.post('/resticker/staging/requeue-unprinted?dry_run=true');
      setRequeuePreview(r.data);
      setRequeueOpen(true);
    } catch (e) {
      toast.error('Re-queue preview failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRequeuing(false);
    }
  };

  const handleRequeueApply = async () => {
    setRequeuing(true);
    try {
      const r = await apiClient.post('/resticker/staging/requeue-unprinted?dry_run=false');
      toast.success(
        `Re-queued ${r.data.requeued} pack(s) across ${r.data.bins_affected} bin(s) for reprint.`,
        { duration: 8000 },
      );
      setRequeueOpen(false);
      setRequeuePreview(null);
      await refresh();
    } catch (e) {
      toast.error('Re-queue failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRequeuing(false);
    }
  };

  // ── Printer connect (WebUSB — same transport as USN Label Reprint) ────
  const connectPrinter = async () => {
    if (!printerApi.isWebUSBSupported()) {
      toast.error('WebUSB not supported. Use Chrome / Edge desktop.');
      return;
    }
    try {
      const device = await printerApi.requestUSBPrinter();
      if (!device) return;
      setPrinter(device);
      toast.success('Printer connected.');
    } catch (e) {
      toast.error('Printer connect cancelled or failed: ' + e.message);
    }
  };

  // ── Print labels for selected bin ────────────────────────────────────
  const handlePrintBin = async () => {
    if (!selectedBin) {
      toast.error('Pick a bin first.');
      return;
    }
    if (!printer && !printerApi.isNetworkPrintActive()) {
      toast.error('Connect the printer before printing.');
      return;
    }

    setPrinting(true);
    let batchId = null;
    let labelsAck = [];
    try {
      // Step 1 — backend claims atomically + commits chain
      toast.info(`Claiming labels for ${selectedBin.bin_code}...`);
      const claim = await apiClient.post('/resticker/print-batch', null, {
        params: { bin_code: selectedBin.bin_code },
      });
      const { batch_id, labels } = claim.data;
      batchId = batch_id;
      if (!labels || labels.length === 0) {
        toast.message('Nothing to print for this bin. Refreshing.');
        await refresh();
        return;
      }

      // Step 2 — build ONE TSPL stream for all labels and send via WebUSB.
      // The backend already resolved canonical label_data + template +
      // mrp_formatted per label (identical to USN Label Reprint), so we
      // feed those straight into the generator — no thin mock, no extra
      // template fetch.
      // Build TSPL only for packs whose product data resolved. Packs missing
      // master data (no rate in rates_master for their barcode/pricing type)
      // come back as label_data=null — we DON'T crash the batch on them and we
      // DON'T print a blank/wrong sticker. They're ACK'd as failed so they
      // bounce back to 'staged' for retry once rates are fixed.
      const printable = [];
      const skipped = [];
      let combinedTspl = '';
      for (const lbl of labels) {
        if (!lbl.label_data) { skipped.push(lbl); continue; }
        printable.push(lbl);
        combinedTspl += generateTsplFromLabelData(
          lbl.label_data,
          lbl.template,
          lbl.new_usn,
          lbl.mrp_formatted || '',
          null,
          PRINTER_CONFIGS.TSC_TE210,
          lbl.template?.template_type,
        );
      }

      if (printable.length === 0) {
        labelsAck = skipped.map(l => ({ fg_inv_id: l.fg_inv_id, printed_ok: false, failure_reason: (l.label_error || 'MISSING_MASTER_DATA').slice(0, 80) }));
        await apiClient.post(`/resticker/print-batch/${batchId}/ack`, { items: labelsAck });
        labelsAck = [];
        const reason = skipped[0]?.label_error || 'no rate/master data found';
        toast.error(`None of the ${labels.length} packs could be printed — ${reason}. They've been returned to staged. (Check rates_master for these barcodes.)`, { duration: 14000 });
        await refresh();
        return;
      }

      toast.info(`Printing ${printable.length} labels${skipped.length ? ` (${skipped.length} skipped — no master data)` : ''}...`);
      labelsAck = [];
      try {
        await printerApi.sendToUSBPrinter(printer, combinedTspl);
        labelsAck = [
          ...printable.map(l => ({ fg_inv_id: l.fg_inv_id, printed_ok: true })),
          ...skipped.map(l => ({ fg_inv_id: l.fg_inv_id, printed_ok: false, failure_reason: (l.label_error || 'MISSING_MASTER_DATA').slice(0, 80) })),
        ];
      } catch (sendErr) {
        labelsAck = labels.map(l => ({
          fg_inv_id: l.fg_inv_id, printed_ok: false,
          failure_reason: (sendErr.message || 'PRINTER_SEND_FAILED').slice(0, 80),
        }));
        throw sendErr;  // outer catch shows the error + ACKs the failures
      }

      // Step 4 — ACK back to backend
      const ack = await apiClient.post(
        `/resticker/print-batch/${batchId}/ack`,
        { items: labelsAck },
      );
      const aR = ack.data;
      toast.success(`Batch ${batchId} · ${aR.succeeded} printed${aR.failed ? `, ${aR.failed} bounced back` : ''}.`, { duration: 7000 });
      if (skipped.length > 0) {
        const sampleBarcodes = [...new Set(skipped.map(l => l.item_barcode).filter(Boolean))].slice(0, 5).join(', ');
        toast.warning(`${skipped.length} pack(s) skipped — no rate/master data${sampleBarcodes ? ` (e.g. ${sampleBarcodes})` : ''}. Fix rates_master and re-print the bin.`, { duration: 12000 });
      }
      setRecentBatches(prev => [{
        batch_id: batchId,
        bin_code: selectedBin.bin_code,
        succeeded: aR.succeeded,
        failed: aR.failed,
        at: new Date().toLocaleTimeString(),
      }, ...prev.slice(0, 9)]);

      // Step 5 — Phase B: box stickers for combo packs (items with a box_template)
      const boxPlans = (labels || []).filter(l => l.box_template && l.label_data);
      if (boxPlans.length > 0) {
        await runBoxStickerPhase(boxPlans);
      }

      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error('Print batch failed: ' + detail, { duration: 9000 });
      // If backend hadn't ACK'd yet and we have labelsAck, send what we have so labels don't stay stuck in 'printing'
      if (batchId && labelsAck.length > 0) {
        try {
          await apiClient.post(`/resticker/print-batch/${batchId}/ack`, { items: labelsAck });
        } catch {/* swallow */}
      }
    } finally {
      setPrinting(false);
    }
  };

  // ── Phase B — box stickers for combo packs ───────────────────────────
  // Opens a prompt to pick ONE box template for the batch (pre-filled with
  // the most common item-configured box_template) and connect a separate
  // box printer, then streams the box-sticker blob.
  const runBoxStickerPhase = async (boxPlans) => {
    // Suggest the most common configured box template across the combo packs.
    const counts = {};
    for (const bp of boxPlans) {
      const nm = bp.box_template?.template_name;
      if (nm) counts[nm] = (counts[nm] || 0) + 1;
    }
    const suggestedName = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
    const suggestedId = templates.find(t => t.template_name === suggestedName)?.template_id || '';

    const phaseB = await new Promise((resolve) => {
      setBoxDialogCount(boxPlans.length);
      setBoxTemplateId(suggestedId);
      setBoxDevice(null);
      setBoxResolve(() => resolve);
      setBoxDialogOpen(true);
    });

    if (!phaseB || phaseB.cancelled) {
      toast.info(`Box stickers skipped — ${boxPlans.length} combo box label(s) NOT printed.`);
      return;
    }

    const device = phaseB.device;
    if (!device) {
      toast.error('No box printer connected — box stickers not printed.');
      return;
    }
    const hasFields = (t) => !!(t && t.fields && Object.keys(t.fields).length > 0);
    const chosenTpl = templates.find(t => t.template_id === phaseB.templateId);
    let boxBlob = '';
    let skippedBox = 0;
    for (const bp of boxPlans) {
      // The override template only wins if it actually has a field layout.
      // Otherwise use the item's configured box template. NEVER render with a
      // field-less template — the generator would silently fall back to the
      // narrow product-default coordinates and cram everything (overlap bug).
      const tpl = hasFields(chosenTpl) ? chosenTpl : bp.box_template;
      if (!hasFields(tpl) || !bp.label_data) { skippedBox++; continue; }
      boxBlob += generateTsplFromLabelData(
        bp.label_data, tpl, bp.new_usn, bp.mrp_formatted || '',
        null, PRINTER_CONFIGS.TSC_TE210, tpl?.template_type,
      );
    }
    if (!boxBlob) {
      toast.error('No printable box stickers — the chosen template has no field layout. Pick a configured box template (e.g. "SMALL BOX OUTER - FG").', { duration: 10000 });
      return;
    }
    try {
      await printerApi.sendToUSBPrinter(device, boxBlob);
      const printed = boxPlans.length - skippedBox;
      toast.success(`Printed ${printed} box sticker(s).${skippedBox ? ` ${skippedBox} skipped (no template layout).` : ''}`, { duration: 6000 });
    } catch (e) {
      toast.error(`Box-sticker print error: ${e.message || e}. Product stickers were already printed.`, { duration: 9000 });
    }
  };

  const connectBoxPrinter = async () => {
    if (!printerApi.isWebUSBSupported()) {
      toast.error('WebUSB not supported. Use Chrome / Edge desktop.');
      return;
    }
    try {
      const device = await printerApi.requestUSBPrinter();
      if (!device) return;
      setBoxDevice(device);
      toast.success('Box printer connected.');
    } catch (e) {
      toast.error('Box printer connect cancelled or failed: ' + e.message);
    }
  };

  const confirmBoxPrint = () => {
    if (boxResolve) boxResolve({ templateId: boxTemplateId, device: boxDevice });
    setBoxDialogOpen(false);
    setBoxResolve(null);
  };

  const cancelBoxPrint = () => {
    if (boxResolve) boxResolve({ cancelled: true });
    setBoxDialogOpen(false);
    setBoxResolve(null);
  };

  // ── Export bins (CSV) ────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const handleExportBins = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get('/resticker/staging/bins/export', {
        params: binSearch.trim() ? { q: binSearch.trim() } : {},
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk_resticker_bins_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bin list exported.');
    } catch (e) {
      toast.error('Export failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  // ── UI ───────────────────────────────────────────────────────────────
  const ageDays = useMemo(() => (iso) => {
    if (!iso) return null;
    return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24)));
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bulk Resticker</h1>
          <p className="text-sm text-gray-500">Pre-allocate Replace USNs, then print bin-by-bin.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={handleRequeuePreview}
              disabled={requeuing}
              data-testid="requeue-unprinted-btn"
              title="Re-queue packs that were marked printed but whose label never came out"
            >
              {requeuing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Re-queue Unprinted
            </Button>
          )}
          <Button
            onClick={handleTopUp}
            disabled={toppingUp}
            data-testid="top-up-btn"
          >
            {toppingUp ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
            Top-up Staging Now
          </Button>
        </div>
      </div>

      {/* Summary */}
      <Card>
        <CardHeader><CardTitle className="text-base">Staging Pool</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-4 gap-4 text-center">
          <Stat label="Staged" value={summary?.staged ?? '—'} tone="emerald" testid="stat-staged" />
          <Stat label="Printing" value={summary?.printing ?? '—'} tone="amber" testid="stat-printing" />
          <Stat label="Printed" value={summary?.printed ?? '—'} tone="blue" testid="stat-printed" />
          <Stat label="Consumed" value={summary?.consumed ?? '—'} tone="gray" testid="stat-consumed" />
        </CardContent>
      </Card>

      {/* Design Transfer via staging */}
      <Card className="border-[#2D5F4F]/30" data-testid="design-transfer-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-[#2D5F4F]" />
            Design Transfer (via Staging)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Upload a <strong>source_barcode,target_barcode</strong> CSV, then scan the source USNs.
            Each scan is <strong>queued instantly</strong> (no per-scan wait). When you're done, click
            <strong> Mint Pending</strong> to mint all the new USNs in one batch, then pick the bin and print.
            Destination bin is assigned later via <strong>Table Clearance</strong>.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={dtFileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleUploadDtCsv}
              data-testid="dt-csv-file-input"
            />
            <Button
              variant="outline"
              onClick={() => dtFileRef.current?.click()}
              disabled={dtUploading}
              data-testid="dt-upload-csv-btn"
            >
              {dtUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Upload Mapping CSV
            </Button>
            <Badge variant={dtMapping?.mapping_count ? 'default' : 'secondary'} data-testid="dt-mapping-count">
              {dtMapping?.mapping_count ? `${dtMapping.mapping_count} SKU pair(s) active` : 'No mapping uploaded'}
            </Badge>
            {dtMapping?.uploaded_by && (
              <span className="text-xs text-gray-400">by {dtMapping.uploaded_by}</span>
            )}
          </div>

          <div className="flex gap-2 max-w-md">
            <Input
              ref={dtScanInputRef}
              value={dtScanInput}
              onChange={(e) => setDtScanInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleDtScan(); }}
              placeholder="Scan source USN…"
              disabled={dtScanning || !dtMapping?.mapping_count}
              data-testid="dt-scan-input"
            />
            <Button
              onClick={() => handleDtScan()}
              disabled={dtScanning || !dtMapping?.mapping_count}
              data-testid="dt-scan-btn"
            >
              {dtScanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Queue'}
            </Button>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant={dtPendingCount ? 'default' : 'secondary'} data-testid="dt-pending-count">
              {dtPendingCount} queued for mint
            </Badge>
            <Button
              onClick={handleMintBatch}
              disabled={dtMinting || dtPendingCount === 0}
              className="bg-[#2D5F4F] hover:bg-[#234a3e]"
              data-testid="dt-mint-batch-btn"
            >
              {dtMinting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
              Mint Pending {dtPendingCount ? `(${dtPendingCount})` : ''}
            </Button>
          </div>

          {dtResults.length > 0 && (
            <div className="rounded-md border max-h-[240px] overflow-y-auto text-sm" data-testid="dt-results">
              {dtResults.map((r, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-1.5 border-b last:border-0 ${r.ok ? '' : 'bg-red-50'}`}>
                  {r.ok ? (
                    <>
                      <span className="font-mono text-xs">{r.queued && !r.new_usn ? r.old_usn : `${r.old_usn} → ${r.new_usn}`}</span>
                      <span className="flex items-center gap-2 text-gray-600 text-xs">
                        <span>{r.source_barcode} → {r.target_barcode}</span>
                        <span
                          className="inline-flex items-center rounded bg-amber-100 text-amber-800 font-medium px-1.5 py-0.5"
                          data-testid="dt-row-source-bin"
                        >
                          Bin: {r.bin_code || 'UNASSIGNED'}
                        </span>
                        {r.queued && !r.new_usn && <span className="text-emerald-700">queued</span>}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-xs">{r.old_usn}</span>
                      <span className="text-red-600 text-xs">{r.error}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bin picker + print */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pick a bin to print</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search bin code (e.g., B-A-12)"
              value={binSearch}
              onChange={(e) => setBinSearch(e.target.value)}
              data-testid="bin-search"
            />
            <Button variant="outline" onClick={refresh} data-testid="bin-search-go">
              Search
            </Button>
            <Button
              variant="outline"
              onClick={handleExportBins}
              disabled={exporting || bins.length === 0}
              data-testid="export-bins-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Export Bins
            </Button>
          </div>

          <div className="border rounded-md max-h-96 overflow-y-auto">
            {bins.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-500">
                No bins with staged labels. Click <span className="font-semibold">Top-up Staging</span> first.
              </div>
            )}
            {bins.map((b) => {
              const days = ageDays(b.oldest_staged_at);
              const selected = selectedBin?.bin_code === b.bin_code;
              const ageColor = days >= 30 ? 'bg-red-50 text-red-700' :
                               days >= 7  ? 'bg-amber-50 text-amber-700' :
                                            'bg-emerald-50 text-emerald-700';
              return (
                <button
                  key={b.bin_code}
                  onClick={() => setSelectedBin(b)}
                  className={`w-full flex items-center justify-between px-4 py-2 text-left border-b last:border-0 hover:bg-gray-50 ${selected ? 'bg-blue-50' : ''}`}
                  data-testid={`bin-row-${b.bin_code}`}
                >
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-gray-400" />
                    <span className="font-mono text-sm">{b.bin_code}</span>
                    <Badge variant="outline">{b.staged_count} pack{b.staged_count === 1 ? '' : 's'}</Badge>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${ageColor}`}>
                    {days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} old`}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 border-t">
            <div className="text-sm flex items-center gap-2">
              <PrintMethodToggle screenKey="bulk-resticker" />
              {printer ? (
                <span className="text-emerald-700">✓ Printer connected</span>
              ) : (
                <Button size="sm" variant="outline" onClick={connectPrinter} data-testid="connect-printer-btn">
                  <Printer className="w-4 h-4 mr-1" /> Connect printer
                </Button>
              )}
            </div>
            <Button
              size="lg"
              onClick={handlePrintBin}
              disabled={!selectedBin || !printer || printing}
              data-testid="print-bin-btn"
            >
              {printing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
              {selectedBin
                ? `Print ${selectedBin.staged_count} for ${selectedBin.bin_code}`
                : 'Pick a bin'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent batches */}
      {recentBatches.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Recent batches</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {recentBatches.map(b => (
              <div key={b.batch_id} className="flex items-center gap-3 text-sm" data-testid={`recent-batch-${b.batch_id}`}>
                <span className="font-mono text-xs">{b.batch_id}</span>
                <span className="text-gray-500">·</span>
                <span className="font-mono">{b.bin_code}</span>
                <span className="text-gray-500">·</span>
                <span className="text-emerald-700">{b.succeeded} ✓</span>
                {b.failed > 0 && (
                  <>
                    <span className="text-gray-500">·</span>
                    <span className="text-amber-700">{b.failed} bounced ⚠</span>
                  </>
                )}
                <span className="ml-auto text-gray-400 text-xs">{b.at}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 flex gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <p><strong>How this page works:</strong></p>
          <ol className="list-decimal pl-5 space-y-0.5 mt-1">
            <li>Click <b>Top-up Staging</b> after each batch of price revisions.</li>
            <li>Connect your TSPL printer.</li>
            <li>Search / pick a bin from the list. The dropdown shows oldest bins first.</li>
            <li>Click <b>Print N for &lt;BIN&gt;</b>. Labels print and the new USN chain is committed instantly.</li>
            <li>If the printer jams part-way, the un-printed labels bounce back to <i>staged</i>. Just click Print again.</li>
          </ol>
        </div>
      </div>

      {/* Phase B — box-sticker prompt (combo packs only) */}      <Dialog open={boxDialogOpen} onOpenChange={(o) => { if (!o) cancelBoxPrint(); }}>
        <DialogContent data-testid="box-sticker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Box className="w-5 h-5" /> Print Box Stickers
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600" data-testid="box-dialog-count">
              {boxDialogCount} combo pack{boxDialogCount === 1 ? '' : 's'} in this batch need a box sticker.
              Pick the box template and connect the box printer.
            </p>

            <div className="space-y-1">
              <label className="text-sm font-medium">Box template</label>
              <Select value={boxTemplateId} onValueChange={setBoxTemplateId}>
                <SelectTrigger data-testid="box-template-select">
                  <SelectValue placeholder="Select a box template" />
                </SelectTrigger>
                <SelectContent>
                  {templates
                    .filter(t => t.fields && Object.keys(t.fields).length > 0)
                    .map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.template_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400">
                Defaulted to each item's configured box template — override here to use one template for the whole batch.
              </p>
            </div>

            <div className="flex items-center justify-between border rounded-md p-3">
              <span className="text-sm">
                {boxDevice
                  ? <span className="text-emerald-700">✓ Box printer connected</span>
                  : <span className="text-gray-500">No box printer connected</span>}
              </span>
              <Button size="sm" variant="outline" onClick={connectBoxPrinter} data-testid="connect-box-printer-btn">
                <Printer className="w-4 h-4 mr-1" /> {boxDevice ? 'Reconnect' : 'Connect box printer'}
              </Button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={cancelBoxPrint} data-testid="box-skip-btn">Skip box stickers</Button>
            <Button
              onClick={confirmBoxPrint}
              disabled={!boxDevice || !boxTemplateId}
              data-testid="box-print-btn"
            >
              <Printer className="w-4 h-4 mr-1" /> Print {boxDialogCount} box sticker{boxDialogCount === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin — re-queue unprinted labels confirm */}
      <Dialog open={requeueOpen} onOpenChange={(o) => { if (!o) { setRequeueOpen(false); setRequeuePreview(null); } }}>
        <DialogContent data-testid="requeue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5" /> Re-queue Unprinted Labels
            </DialogTitle>
            <DialogDescription>
              Re-stage packs whose new sticker was committed but never confirmed as physically printed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 text-sm">
            <p>
              <strong data-testid="requeue-matched">{requeuePreview?.matched_unprinted ?? 0}</strong> pack(s)
              across <strong data-testid="requeue-bins">{requeuePreview?.bins_affected ?? 0}</strong> bin(s)
              are marked <span className="font-medium">printed</span> but can be re-queued for reprint
              (their new sticker was committed but never confirmed as physically printed).
            </p>
            <p className="text-xs text-gray-500">
              They'll re-appear in the bin list below, under their current bin. This reuses the
              already-assigned USNs — no new USNs are minted.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setRequeueOpen(false); setRequeuePreview(null); }} data-testid="requeue-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleRequeueApply}
              disabled={requeuing || !(requeuePreview?.matched_unprinted)}
              data-testid="requeue-confirm-btn"
            >
              {requeuing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Re-queue {requeuePreview?.matched_unprinted ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    gray: 'bg-gray-50 text-gray-700',
  };
  return (
    <div className={`rounded-md p-3 ${tones[tone] || tones.gray}`} data-testid={testid}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs opacity-80">{label}</div>
    </div>
  );
}
